/**
 * TanStack Query key factory (04-architecture § 8.4).
 *
 * Keys are arrays, most general segment first, so mutations can invalidate by
 * prefix. Nothing outside this file builds a query key.
 */

export const qk = {
  meta: () => ['meta'] as const,

  public: {
    site: () => ['public', 'site'] as const,
    catalog: () => ['public', 'catalog'] as const,
    locations: () => ['public', 'locations'] as const,
    availability: () => ['public', 'availability'] as const,
    desk: (deskCode: string) => ['public', 'desk', deskCode] as const,
  },

  me: () => ['me'] as const,
  paymentMethod: () => ['me', 'payment-method'] as const,
  contracts: () => ['me', 'contracts'] as const,
  invoices: () => ['me', 'invoices'] as const,
  credit: () => ['me', 'credit'] as const,

  doorDevices: () => ['door', 'devices'] as const,

  locations: () => ['locations'] as const,
  plan: (locationId: string) => ['locations', locationId, 'plan'] as const,
  availability: (locationId: string, day: string) => ['locations', locationId, 'availability', day] as const,
  availabilityRange: (locationId: string, from: string, days: number) =>
    ['locations', locationId, 'availability', 'range', from, days] as const,
  occupancy: (locationId: string) => ['locations', locationId, 'occupancy'] as const,
  rooms: (locationId: string, day: string) => ['locations', locationId, 'rooms', day] as const,
  terminalStatus: (locationId: string) => ['locations', locationId, 'terminals'] as const,

  bookings: (scope: 'upcoming' | 'past') => ['bookings', scope] as const,

  quote: (itemsHash: string) => ['quote', itemsHash] as const,
  order: (orderId: string) => ['orders', orderId] as const,

  notifications: () => ['notifications'] as const,
  unreadCount: () => ['notifications', 'unread-count'] as const,

  team: (teamId: string) => ['teams', teamId] as const,

  admin: {
    area: (area: string, params?: unknown) =>
      params === undefined ? (['admin', area] as const) : (['admin', area, params] as const),
  },
} as const;

/** `staleTime` per key group (04 § 8.4). */
export const STALE_TIME = {
  meta: 10 * 60_000,
  publicSite: 60 * 60_000,
  publicCatalog: 60 * 60_000,
  publicLocations: 30_000,
  publicAvailability: 60_000,
  me: 30_000,
  paymentMethod: 5 * 60_000,
  contracts: 60_000,
  invoices: 5 * 60_000,
  credit: 60_000,
  doorDevices: 5 * 60_000,
  locations: 5 * 60_000,
  /**
   * `/locations` reporting a new version refetches the plan; on its own it goes
   * stale after hours only, well inside the day its signed image link lives
   * (the query is persisted across launches).
   */
  plan: 6 * 60 * 60_000,
  availability: 15_000,
  availabilityRange: 60_000,
  occupancy: 15_000,
  rooms: 30_000,
  terminalStatus: 30_000,
  bookings: 30_000,
  quote: 0,
  notifications: 60_000,
  admin: 30_000,
} as const;

/** A stable hash of the checkout items, so `qk.quote` changes when they do. */
export function hashItems(items: unknown): string {
  return JSON.stringify(items);
}
